/**
 * Sozlamalar's shell, 1:1 with K3: a 220px menu beside the section from the width where both fit,
 * and a labelled select above it below that. A role with no section sees "Ruxsat yo'q", the same
 * answer every other guarded screen gives.
 */
import type { JSX } from "react";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router";

import type { Role } from "@/api/types";
import { useTranslation } from "@/i18n";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/auth";
import { NoPermission } from "@/ui/states";

import { legacySectionTarget, settingsNavFor, settingsNavLines } from "./settingsNav";

const ACTIVE_RULE = { boxShadow: "inset 2px 0 0 var(--k-accent)" };

function useNavItems(): ReturnType<typeof settingsNavFor> {
  const role: Role | undefined = useAuthStore((state) => state.user?.role ?? undefined);
  return settingsNavFor(role);
}

export function SettingsLayout(): JSX.Element {
  const { t } = useTranslation();
  const items = useNavItems();
  const location = useLocation();
  const navigate = useNavigate();
  const role = useAuthStore((state) => state.user?.role ?? undefined);
  const current = items.find((item) => location.pathname.startsWith(item.to));

  // No menu to sit beside: the refusal takes the whole page, the same as every guarded screen.
  if (items.length === 0) return <Outlet />;

  return (
    <div className="mx-auto grid w-full max-w-310 grid-cols-1 items-start gap-6 xl:grid-cols-[220px_minmax(0,1fr)]">
      <>
          <label className="flex flex-col gap-1.5 xl:hidden">
            <span className="text-xs font-medium text-ink-muted">{t("soz.section")}</span>
            <select
              aria-label={t("soz.sectionLabel")}
              className="h-11 w-full rounded-control border border-line bg-surface px-3 text-base text-ink"
              value={current?.to ?? ""}
              onChange={(event) => {
                void navigate(event.target.value);
              }}
            >
              {items.map((item) => (
                <option key={item.key} value={item.to}>
                  {role === "owner" ? `${t(item.groupKey)} · ${t(item.labelKey)}` : t(item.labelKey)}
                </option>
              ))}
            </select>
          </label>
          <nav aria-label={t("soz.navLabel")} className="sticky top-18 hidden flex-col gap-0.5 xl:flex">
            <span className="px-2.5 pb-2.5 pt-1 text-xs font-medium text-ink-subtle">{t("soz.title")}</span>
            {settingsNavLines(items).map((line) =>
              "heading" in line ? (
                <span key={line.heading} className="flex h-8 items-end px-2.5 pb-1 text-xs font-medium text-ink-subtle">
                  {t(line.heading)}
                </span>
              ) : (
                <NavLink
                  key={line.item.key}
                  to={line.item.to}
                  style={({ isActive }) => (isActive ? ACTIVE_RULE : undefined)}
                  className={({ isActive }) =>
                    cn(
                      "flex h-8 items-center whitespace-nowrap rounded-small px-2.5 text-sm",
                      isActive
                        ? "bg-accent-soft font-medium text-accent-ink"
                        : "text-ink-muted hover:bg-surface-muted hover:text-ink",
                    )
                  }
                >
                  {t(line.item.labelKey)}
                </NavLink>
              ),
            )}
          </nav>
      </>
      <div className="min-w-0">
        <Outlet />
      </div>
    </div>
  );
}

/** `/sozlamalar` → the first section the caller may open, or the one an older link names. */
export function SettingsIndexRedirect(): JSX.Element {
  const { t } = useTranslation();
  const items = useNavItems();
  const location = useLocation();
  const navigate = useNavigate();
  const target = legacySectionTarget(items, new URLSearchParams(location.search));
  if (target === undefined) {
    return (
      <NoPermission
        page
        message={t("soz.denied")}
        home={{
          label: t("ui.denied.home"),
          onClick: (): void => {
            void navigate("/");
          },
        }}
      />
    );
  }
  return <Navigate to={{ pathname: target.to, search: location.search }} replace />;
}
